/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { RescheduleSessionSchema, type RescheduleSessionInput } from '@therapy/shared-validators';
import { type SyntheticEvent, useCallback, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '../../components/form/form-field.js';
import { Button } from '../../components/ui/button.js';
import { useRescheduleSession } from '../../lib/queries/sessions.js';
import { toDatetimeLocalIst } from '../../lib/utils/datetime.js';

interface RescheduleFormProps {
  readonly sessionId: string;
  /** Current `scheduledStart` (ISO), used to prefill the picker. */
  readonly currentStart: string;
  readonly onDone: () => void;
}

/**
 * "Reschedule" form (Figma). Moves a still-scheduled session to a new time;
 * the API rejects a slot that clashes with the therapist's other sessions.
 */
export function RescheduleForm({
  sessionId,
  currentStart,
  onDone,
}: RescheduleFormProps): JSX.Element {
  const { t } = useTranslation();
  const reschedule = useRescheduleSession();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RescheduleSessionInput>({
    resolver: zodResolver(RescheduleSessionSchema),
    defaultValues: { scheduledStart: toDatetimeLocalIst(currentStart) },
  });

  const submit = useCallback(
    async (values: RescheduleSessionInput): Promise<void> => {
      await reschedule.mutateAsync({
        id: sessionId,
        scheduledStart: new Date(values.scheduledStart).toISOString(),
      });
      onDone();
    },
    [reschedule, sessionId, onDone],
  );

  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  return (
    <form onSubmit={onFormSubmit} className="space-y-4" noValidate>
      <FormField
        id="reschedule-start"
        type="datetime-local"
        label={t('form.reschedule.start')}
        autoComplete="off"
        error={errors.scheduledStart?.message}
        registration={register('scheduledStart')}
      />

      {reschedule.isError && (
        <p role="alert" className="text-sm text-red-600">
          {t('form.reschedule.conflict')}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting || reschedule.isPending}>
          {t('form.reschedule.submit')}
        </Button>
      </div>
    </form>
  );
}
